import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/utils/api';
import { Check, Crown, Zap, Building2, ArrowRight, ShieldCheck, Download, Lock, Loader2 } from 'lucide-react';
import Layout from '@/components/Layout';

// ─────────────────────────────────────────────────────────────────────────────
// Compliance card — PDPL data export + TOTP 2FA
// ─────────────────────────────────────────────────────────────────────────────
function ComplianceCard({ isAr }) {
  const [twoFA, setTwoFA] = useState({ two_factor_enabled: false });
  const [setupData, setSetupData] = useState(null);    // { secret, otpauth_uri }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  useEffect(() => { api.twoFAStatus().then(r => setTwoFA(r.data)).catch(() => {}); }, []);

  const exportData = async () => {
    setBusy(true);
    try {
      const res = await api.exportMyData();
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reva-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setFlash(isAr ? 'تم تنزيل البيانات' : 'Export downloaded');
    } catch (e) { console.error(e); }
    finally { setBusy(false); setTimeout(() => setFlash(''), 2500); }
  };

  const start2FA = async () => {
    setBusy(true);
    try {
      const res = await api.twoFASetup();
      setSetupData(res.data);
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  };

  const enable2FA = async () => {
    setBusy(true);
    try {
      const res = await api.twoFAEnable(code);
      setRecoveryCodes(res.data.recovery_codes);
      setTwoFA({ two_factor_enabled: true });
      setSetupData(null);
      setCode('');
      setFlash(isAr ? 'تم تفعيل المصادقة الثنائية' : '2FA enabled');
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); setTimeout(() => setFlash(''), 2500); }
  };

  const disable2FA = async () => {
    if (!window.confirm(isAr ? 'تعطيل المصادقة الثنائية؟ سيلزم رمز التحقق الحاليّ.' : 'Disable 2FA? Enter your current code.')) return;
    const c = window.prompt(isAr ? 'أدخل رمز المصادقة الحاليّ' : 'Enter current 6-digit code');
    if (!c) return;
    setBusy(true);
    try {
      await api.twoFADisable(c);
      setTwoFA({ two_factor_enabled: false });
      setRecoveryCodes(null);
      setFlash(isAr ? 'تم تعطيل المصادقة الثنائية' : '2FA disabled');
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); setTimeout(() => setFlash(''), 2500); }
  };

  const qrSrc = setupData
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=${encodeURIComponent(setupData.otpauth_uri)}`
    : null;

  return (
    <Card className="shadow-soft border-gray-100" data-testid="settings-compliance-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-heading text-base">
          <ShieldCheck className="h-4 w-4 text-primary" />
          {isAr ? 'الأمان والامتثال' : 'Security & compliance'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Data export */}
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 pb-5">
          <div className="flex-1">
            <div className="font-medium text-sm text-gray-900">
              {isAr ? 'تصدير بياناتي (PDPL)' : 'Export my data (PDPL)'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {isAr ? 'تنزيل نسخة JSON من جميع بيانات منظّمتك — العقارات، الوحدات، العقود، المستأجرون، الدفعات.' : 'Download a JSON snapshot of every record in your organization — properties, units, leases, tenants, payments.'}
            </div>
          </div>
          <Button onClick={exportData} disabled={busy} size="sm" variant="outline" className="rounded-xl" data-testid="settings-export-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin me-1.5" /> : <Download className="h-3.5 w-3.5 me-1.5" />}
            {isAr ? 'تنزيل' : 'Download'}
          </Button>
        </div>

        {/* 2FA */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="font-medium text-sm text-gray-900 flex items-center gap-2">
              <Lock className="h-3.5 w-3.5 text-gray-500" />
              {isAr ? 'المصادقة الثنائيّة (TOTP)' : 'Two-factor authentication (TOTP)'}
              {twoFA.two_factor_enabled && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                  {isAr ? 'مفعَّل' : 'On'}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {isAr
                ? 'اربط حسابك بتطبيق مصادقة (Google Authenticator، 1Password، Authy). يُنصح به للمشرفين.'
                : 'Link your account with an authenticator app (Google Authenticator, 1Password, Authy). Recommended for admin roles.'}
            </div>
          </div>
          {twoFA.two_factor_enabled ? (
            <Button onClick={disable2FA} size="sm" variant="outline" className="rounded-xl text-red-600 border-red-200 hover:bg-red-50" data-testid="settings-2fa-disable">
              {isAr ? 'تعطيل' : 'Disable'}
            </Button>
          ) : !setupData ? (
            <Button onClick={start2FA} disabled={busy} size="sm" className="rounded-xl" data-testid="settings-2fa-setup">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin me-1.5" /> : null}
              {isAr ? 'إعداد' : 'Set up'}
            </Button>
          ) : null}
        </div>

        {/* 2FA QR + code input */}
        {setupData && (
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="settings-2fa-setup-panel">
            <div className="text-[12px] text-gray-700">
              {isAr ? '١. امسح هذا الرمز QR في تطبيق المصادقة:' : '1. Scan this QR code with your authenticator app:'}
            </div>
            <div className="flex items-center gap-4">
              {qrSrc && <img src={qrSrc} alt="2FA QR" className="w-32 h-32 border border-gray-200 rounded-lg bg-white p-1" data-testid="settings-2fa-qr" />}
              <div className="text-[10.5px] reva-mono text-gray-600 leading-relaxed break-all">
                {isAr ? 'لا يمكنك مسح الرمز؟ أدخل المفتاح يدويًّا:' : "Can't scan? Enter the key manually:"}
                <br />
                <span className="text-gray-900 select-all">{setupData.secret}</span>
              </div>
            </div>
            <div className="text-[12px] text-gray-700">
              {isAr ? '٢. أدخل الرمز ذا الستّة أرقام من التطبيق:' : '2. Enter the 6-digit code from your app:'}
            </div>
            <div className="flex items-center gap-2">
              <input type="text" inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" className="w-32 px-3 py-2 text-center reva-mono text-lg border border-gray-200 rounded-xl focus:outline-none focus:border-primary tracking-[0.3em]" data-testid="settings-2fa-code" />
              <Button onClick={enable2FA} disabled={busy || code.length !== 6} size="sm" className="rounded-xl" data-testid="settings-2fa-enable">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin me-1.5" /> : <Check className="h-3.5 w-3.5 me-1.5" />}
                {isAr ? 'تفعيل' : 'Enable'}
              </Button>
              <Button onClick={() => { setSetupData(null); setCode(''); }} size="sm" variant="ghost" className="rounded-xl">
                {isAr ? 'إلغاء' : 'Cancel'}
              </Button>
            </div>
          </div>
        )}

        {/* Recovery codes — show once after enabling */}
        {recoveryCodes && (
          <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-4 space-y-2" data-testid="settings-2fa-recovery">
            <div className="text-[12px] font-bold text-amber-900">
              {isAr ? '⚠ احفظ هذه الرّموز الاحتياطيّة في مكان آمن — لن تظهر مرّة أخرى:' : '⚠ Save these recovery codes somewhere safe — they will NOT be shown again:'}
            </div>
            <div className="grid grid-cols-2 gap-2 reva-mono text-[13px] text-amber-950 select-all">
              {recoveryCodes.map(c => <div key={c} className="px-2 py-1 bg-white rounded">{c}</div>)}
            </div>
            <button onClick={() => setRecoveryCodes(null)} className="text-[11px] text-amber-900 underline">
              {isAr ? 'لقد حفظتها' : "I've saved them"}
            </button>
          </div>
        )}

        {flash && <div className="text-[12px] text-primary">{flash}</div>}
      </CardContent>
    </Card>
  );
}

const planIcons = { basic: Building2, pro: Zap, enterprise: Crown };
const planColors = {
  basic: 'border-gray-200',
  pro: 'border-blue-500 ring-2 ring-blue-100',
  enterprise: 'border-purple-500 ring-2 ring-purple-100'
};
const planBadgeColors = {
  basic: 'bg-gray-100 text-gray-700',
  pro: 'bg-blue-100 text-blue-700',
  enterprise: 'bg-purple-100 text-purple-700'
};

export default function Settings() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [plans, setPlans] = useState({});
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [plansRes, currentRes] = await Promise.all([
        api.getSubscriptionPlans(),
        api.getCurrentSubscription()
      ]);
      setPlans(plansRes.data);
      setCurrent(currentRes.data);
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  };

  const handleUpgrade = async (planId) => {
    if (planId === current?.current_tier) return;
    setUpgrading(planId);
    try {
      const res = await api.createSubscriptionCheckout(planId);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('Checkout failed:', error);
    } finally {
      setUpgrading(null);
    }
  };

  const isArabic = i18n.language === 'ar';
  const planOrder = ['basic', 'pro', 'enterprise'];

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold">Subscription & Billing</h1>
          <p className="text-gray-500 mt-1">Manage your plan and billing details</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Current Plan Banner */}
            {current && (
              <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                <CardContent className="pt-6 pb-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${planBadgeColors[current.current_tier]}`}>
                          {current.plan?.name} Plan
                        </span>
                        <span className="text-sm text-gray-500">Current Plan</span>
                      </div>
                      <div className="text-sm text-gray-600 mt-2">
                        Using <strong>{current.unit_count}</strong> of <strong>{current.unit_limit < 9999 ? current.unit_limit : '∞'}</strong> units
                        {current.unit_limit < 9999 && (
                          <span className="ml-2 text-gray-400">({current.usage_pct}%)</span>
                        )}
                      </div>
                      {/* Usage bar */}
                      {current.unit_limit < 9999 && (
                        <div className="w-64 h-2 bg-gray-200 rounded-full mt-2">
                          <div
                            className={`h-2 rounded-full transition-all ${current.usage_pct > 80 ? 'bg-red-500' : current.usage_pct > 50 ? 'bg-yellow-500' : 'bg-blue-500'}`}
                            style={{ width: `${Math.min(current.usage_pct, 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                    {current.subscription && (
                      <div className="text-sm text-right">
                        <div className="text-gray-500">Billing Period</div>
                        <div className="font-medium">
                          {new Date(current.subscription.started_at).toLocaleDateString()} — {new Date(current.subscription.expires_at).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {planOrder.map((planId) => {
                const plan = plans[planId];
                if (!plan) return null;
                const Icon = planIcons[planId];
                const isCurrent = current?.current_tier === planId;
                const features = isArabic ? plan.features_ar : plan.features;

                return (
                  <Card
                    key={planId}
                    data-testid={`plan-card-${planId}`}
                    className={`relative overflow-hidden transition-all hover:shadow-lg ${planColors[planId]} ${isCurrent ? 'shadow-md' : ''}`}
                  >
                    {planId === 'pro' && (
                      <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                        POPULAR
                      </div>
                    )}
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className={`h-6 w-6 ${planId === 'enterprise' ? 'text-purple-600' : planId === 'pro' ? 'text-blue-600' : 'text-gray-600'}`} />
                        <CardTitle className="text-xl">{isArabic ? plan.name_ar : plan.name}</CardTitle>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold">{plan.price}</span>
                        <span className="text-gray-500 text-sm">SAR / month</span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3 mb-6">
                        {features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${planId === 'enterprise' ? 'text-purple-500' : planId === 'pro' ? 'text-blue-500' : 'text-green-500'}`} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {isCurrent ? (
                        <Button disabled className="w-full" variant="outline">
                          Current Plan
                        </Button>
                      ) : (
                        <Button
                          className={`w-full ${planId === 'pro' ? 'bg-blue-600 hover:bg-blue-700' : planId === 'enterprise' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                          onClick={() => handleUpgrade(planId)}
                          disabled={upgrading === planId}
                        >
                          {upgrading === planId ? 'Processing...' : (
                            <>Upgrade <ArrowRight className="h-4 w-4 ml-1" /></>
                          )}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Billing History */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Billing Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-gray-500">Organization</div>
                    <div className="font-medium">{user?.full_name}'s Organization</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Account Email</div>
                    <div className="font-medium">{user?.email}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Current Plan</div>
                    <div className="font-medium capitalize">{current?.current_tier || 'Basic'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Unit Usage</div>
                    <div className="font-medium">{current?.unit_count || 0} / {current?.unit_limit < 9999 ? current?.unit_limit : '∞'}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Change password */}
            <ChangePasswordCard isAr={isArabic} />

            {/* Compliance — PDPL export + 2FA */}
            <ComplianceCard isAr={isArabic} />
          </>
        )}
      </div>
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Change-password card — every authenticated user can change their own password
// ─────────────────────────────────────────────────────────────────────────────
function ChangePasswordCard({ isAr }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setDone(false);
    if (next.length < 8) { setError(isAr ? 'كلمة المرور 8 أحرف على الأقل' : 'Password must be 8+ characters'); return; }
    if (next !== confirm) { setError(isAr ? 'كلمتا المرور غير متطابقتين' : "Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.changeMyPassword({ current_password: current, new_password: next });
      setDone(true);
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      setError(err?.response?.data?.detail || (isAr ? 'فشل تغيير كلمة المرور' : 'Could not change password'));
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="change-password-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-[#00734A]" />
          {isAr ? 'كلمة المرور' : 'Change password'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-[12.5px] text-[#71777E] mb-4">
          {isAr
            ? 'إذا تلقّيت كلمة مرور افتراضيّة من مشرف منصّتك، يمكنك تحديثها هنا.'
            : 'If you were given a default password by your platform admin, you can update it here.'}
        </p>
        <form onSubmit={submit} className="space-y-3 max-w-md">
          <div>
            <label className="text-xs text-[#71777E] font-medium">{isAr ? 'كلمة المرور الحاليّة' : 'Current password'}</label>
            <input type="password" value={current} onChange={e => setCurrent(e.target.value)} required
              className="mt-1 w-full h-9 px-3 border border-[#E6EAF0] rounded-md text-[13px] focus:border-[#00734A] focus:outline-none"
              data-testid="change-password-current" />
          </div>
          <div>
            <label className="text-xs text-[#71777E] font-medium">{isAr ? 'كلمة المرور الجديدة' : 'New password'}</label>
            <input type="password" value={next} onChange={e => setNext(e.target.value)} required minLength={8}
              className="mt-1 w-full h-9 px-3 border border-[#E6EAF0] rounded-md text-[13px] focus:border-[#00734A] focus:outline-none"
              data-testid="change-password-new" />
          </div>
          <div>
            <label className="text-xs text-[#71777E] font-medium">{isAr ? 'تأكيد كلمة المرور' : 'Confirm new password'}</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8}
              className="mt-1 w-full h-9 px-3 border border-[#E6EAF0] rounded-md text-[13px] focus:border-[#00734A] focus:outline-none"
              data-testid="change-password-confirm" />
          </div>
          {error && <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 px-2.5 py-1.5 rounded" data-testid="change-password-error">{error}</div>}
          {done && <div className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded inline-flex items-center gap-1.5" data-testid="change-password-done"><Check size={12} /> {isAr ? 'تم تحديث كلمة المرور' : 'Password updated'}</div>}
          <Button type="submit" disabled={busy || !current || !next} data-testid="change-password-submit">
            {busy ? <Loader2 className="animate-spin h-4 w-4 me-2" /> : <Lock className="h-4 w-4 me-2" />}
            {isAr ? 'تحديث كلمة المرور' : 'Update password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
